import React from 'react';

/**
 * Parses WhatsApp markdown formatting into React nodes.
 * Supports: *bold*, _italic_, ~strikethrough~, ```monospace```, {{N}} variables
 */
export function WhatsAppFormattedText({ text }: { text: string }) {
  if (!text) return null;

  const lines = text.split('\n');

  return (
    <>
      {lines.map((line, lineIndex) => (
        <React.Fragment key={lineIndex}>
          {lineIndex > 0 && <br />}
          {parseLine(line)}
        </React.Fragment>
      ))}
    </>
  );
}

function parseLine(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // Combined regex: ```code```, *bold*, _italic_, ~strike~, {{N}} variables
  const regex = /```([\s\S]*?)```|\*([^*]+)\*|_([^_]+)_|~([^~]+)~|\{\{(\d+)\}\}/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[1] !== undefined) {
      // ```code```
      nodes.push(
        <code key={key++} className="bg-black/10 dark:bg-white/15 text-chat-text px-1 rounded text-[0.85em] font-mono">
          {match[1]}
        </code>
      );
    } else if (match[2] !== undefined) {
      // *bold*
      nodes.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3] !== undefined) {
      // _italic_
      nodes.push(<em key={key++}>{match[3]}</em>);
    } else if (match[4] !== undefined) {
      // ~strikethrough~
      nodes.push(<s key={key++}>{match[4]}</s>);
    } else if (match[5] !== undefined) {
      // {{N}} variable
      nodes.push(
        <span
          key={key++}
          className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1 rounded text-xs font-medium"
        >
          {`{{${match[5]}}}`}
        </span>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}
